import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import {
  OhelCampStripeService,
  OhelCampStripeError,
  OhelCampTurnoProduct,
} from '../../services/ohel-camp-stripe.service';

interface TurnoConfig {
  value: string;
  label: string;
  date: string;
  startDate: string; // YYYY-MM-DD of turno start, for age calculation
  minAge: number;
  maxAge: number;
  description: string;
}

@Component({
  selector: 'app-ohel-camp',
  standalone: true,
  templateUrl: './ohel-camp.component.html',
  styleUrl: './ohel-camp.component.scss',
  imports: [CommonModule, FormsModule],
})
export class OhelCampComponent implements OnInit {
  private stripeService = inject(OhelCampStripeService);
  private meta = inject(Meta);
  private title = inject(Title);

  // Tipo iscrizione
  tipoIscrizione: string = '';

  // Turno
  turno: string = '';

  // Dati partecipante
  cognome: string = '';
  nome: string = '';
  luogoNascita: string = '';
  dataNascita: string = '';
  residenza: string = '';
  via: string = '';
  cap: string = '';
  codiceFiscale: string = '';
  telefono: string = '';
  email: string = '';
  reperibilita: string = '';
  note: string = '';

  // Dati Asaf (solo turno Asaf)
  strumentoAsaf: string = '';
  motivazioneAsaf: string = '';

  // Dati genitore (solo minore)
  nomeGenitore: string = '';
  cognomeGenitore: string = '';
  luogoNascitaGenitore: string = '';
  dataNascitaGenitore: string = '';
  residenzaGenitore: string = '';
  viaGenitore: string = '';
  capGenitore: string = '';
  emailGenitore: string = '';

  // Scheda sanitaria
  vaccinazioneAntitetanica: string = '';
  allergie: string = '';
  farmaci: string = '';
  patologie: string = '';
  noteSanitarie: string = '';

  // Consensi
  consensoSocial: string = '';
  consensoTrattamentoDati: boolean = false;
  consensoDiffusioneDati: boolean = false;
  consensoPromozione: boolean = false;
  consensoTrattamentoDatiSanitari: boolean = false;
  consensoEsonero: boolean = false;
  consensoWhatsapp: boolean = false;

  // Document IDs
  documentoIdentitaFronte: string = '';
  documentoIdentitaRetro: string = '';
  tesseraSanitariaFronte: string = '';
  tesseraSanitariaRetro: string = '';
  documentoIdentitaFronteGenitore: string = '';
  documentoIdentitaRetroGenitore: string = '';
  tesseraSanitariaFronteGenitore: string = '';
  tesseraSanitariaRetroGenitore: string = '';

  // Upload UI state
  uploadingSlot: string = '';
  uploadErrors: Record<string, string> = {};
  uploadPreviews: Record<string, string> = {};
  uploadFilenames: Record<string, string> = {};

  // Privacy
  showPrivacyDialog: boolean = false;

  // WhatsApp help button
  showWhatsappBtn: boolean = false;
  whatsappTooltipDismissed: boolean = false;
  readonly WHATSAPP_NUMBER = '393389579944';

  get whatsappUrl(): string {
    const greeting = this.nome
      ? `Ciao, sono ${this.nome}, avrei`
      : 'Ciao, avrei';
    const text = encodeURIComponent(
      `${greeting} bisogno di assistenza per l'iscrizione all'Ohel Camp 2026`,
    );
    return `https://wa.me/${this.WHATSAPP_NUMBER}?text=${text}`;
  }

  // UI state
  isSubmitting: boolean = false;
  isSuccess: boolean = false;
  successMessage: string = '';
  errorMessage: string = '';
  canRetry: boolean = false;

  // Stripe products
  turnoProducts: OhelCampTurnoProduct[] = [];
  isLoadingProducts: boolean = true;
  productsError: string = '';

  // Step del form (6 steps)
  currentStep: number = 1;

  readonly turni: TurnoConfig[] = [
    {
      value: 'children',
      label: 'Children',
      date: '15-20 Giugno',
      startDate: '2026-06-15',
      minAge: 8,
      maxAge: 10,
      description: 'Turno diurno per bambini',
    },
    {
      value: 'kids',
      label: 'Kids',
      date: '22-28 Giugno',
      startDate: '2026-06-22',
      minAge: 11,
      maxAge: 13,
      description: 'Turno per pre-adolescenti',
    },
    {
      value: 'sorrisi-speciali',
      label: 'Sorrisi Speciali',
      date: '29-04 Luglio',
      startDate: '2026-06-29',
      minAge: 8,
      maxAge: 99,
      description: 'Turno dedicato per ragazzi con disabilità o autismo',
    },
    {
      value: 'teenagers',
      label: 'Teenagers',
      date: '6-12 Luglio',
      startDate: '2026-07-06',
      minAge: 14,
      maxAge: 17,
      description: 'Turno per adolescenti',
    },
    {
      value: 'vocazionale',
      label: 'Vocazionale',
      date: '16-19 Luglio',
      startDate: '2026-07-16',
      minAge: 18,
      maxAge: 99,
      description: 'Per la chiamata missionaria',
    },
    {
      value: 'young',
      label: 'Young',
      date: '20-26 Luglio',
      startDate: '2026-07-20',
      minAge: 18,
      maxAge: 25,
      description: 'Turno per giovani',
    },
    {
      value: 'asaf',
      label: 'Asaf',
      date: '3-9 Agosto',
      startDate: '2026-08-03',
      minAge: 0,
      maxAge: 99,
      description: 'Turno per musicisti e adoratori',
    },
  ];

  ngOnInit() {
    // URL base dinamico
    const baseUrl = window.location.origin;
    const imageUrl = `${baseUrl}/images/ohel-camp-share.jpg`;
    const pageUrl = `${baseUrl}/ohelcamp`;

    // SEO - Title (visibile)
    this.title.setTitle(
      'Ohel Summer Camp 2026 - Campeggio Estivo | Come Tralci',
    );

    // SEO - Meta description (nascosta, usa "centro estivo" per SEO)
    this.meta.updateTag({
      name: 'description',
      content:
        'Tenda Ohel: campeggio cristiano evangelico e centro estivo per bambini e ragazzi della chiesa Come Tralci. Turni da giugno ad agosto 2026 con attività, giochi e insegnamenti biblici.',
    });

    // SEO - Keywords (nascosta)
    this.meta.updateTag({
      name: 'keywords',
      content:
        'Ohel Camp, Tenda Ohel, campeggio cristiano, centro estivo cristiano, campo evangelico, campeggio pentecostale, Come Tralci, chiesa evangelica, campo missionario, centro estivo ragazzi, estate 2026',
    });

    // Open Graph - Immagine per condivisione WhatsApp/social
    this.meta.updateTag({
      property: 'og:image',
      content: imageUrl,
    });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Ohel Summer Camp 2026 - Come Tralci',
    });

    // Open Graph - Titolo (visibile su social)
    this.meta.updateTag({
      property: 'og:title',
      content: 'Ohel Summer Camp 2026 - Campeggio Estivo',
    });
    this.meta.updateTag({
      property: 'og:description',
      content:
        'Campeggio cristiano per bambini e ragazzi. Turni da giugno ad agosto 2026. Iscrizioni aperte!',
    });
    this.meta.updateTag({ property: 'og:type', content: 'event' });
    this.meta.updateTag({
      property: 'og:url',
      content: pageUrl,
    });
    this.meta.updateTag({
      property: 'og:site_name',
      content: 'Come Tralci',
    });
    this.meta.updateTag({ property: 'og:locale', content: 'it_IT' });

    // Twitter Card
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: 'Ohel Summer Camp 2026 - Campeggio Estivo',
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content: 'Campeggio cristiano per bambini e ragazzi - Come Tralci',
    });
    this.meta.updateTag({
      name: 'twitter:image',
      content: imageUrl,
    });

    // Structured Data per Google (nascosto)
    this.addStructuredData(baseUrl, imageUrl, pageUrl);

    this.loadProducts();
  }

  public throwTestError(): void {
    throw new Error('Sentry Test Error');
  }

  private addStructuredData(
    baseUrl: string,
    imageUrl: string,
    pageUrl: string,
  ): void {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: 'Ohel Summer Camp 2026',
      description:
        'Centro estivo e campeggio cristiano per bambini e ragazzi organizzato dalla chiesa evangelica Come Tralci. Turni da giugno ad agosto 2026 con attività ludiche, sportive e insegnamenti biblici.',
      image: imageUrl,
      startDate: '2026-06-15',
      endDate: '2026-08-09',
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: {
        '@type': 'Place',
        name: 'Tenda Ohel',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Borgo San Giovanni',
          addressRegion: 'Lombardia',
          addressCountry: 'IT',
        },
      },
      organizer: {
        '@type': 'Organization',
        name: 'Come Tralci',
        url: baseUrl,
      },
      offers: {
        '@type': 'Offer',
        url: pageUrl,
        price: '110',
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        validFrom: '2026-01-01',
      },
    };

    let script = document.querySelector(
      'script[type="application/ld+json"]',
    ) as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }

  loadProducts(): void {
    this.isLoadingProducts = true;
    this.productsError = '';

    this.stripeService.getProducts().subscribe({
      next: (response) => {
        this.isLoadingProducts = false;
        if (response.success) {
          this.turnoProducts = response.turnoProducts;
        } else {
          this.productsError =
            response.error || 'Errore nel caricamento dei prodotti';
        }
      },
      error: (error: OhelCampStripeError) => {
        this.isLoadingProducts = false;
        this.productsError =
          error.message || 'Errore nel caricamento dei prodotti';
      },
    });
  }

  get isMinore(): boolean {
    if (this.tipoIscrizione === 'minore') return true;
    if (this.tipoIscrizione === 'adulto' && this.dataNascita) {
      const birth = new Date(this.dataNascita);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--;
      }
      return age < 18;
    }
    return false;
  }

  get selectedTurno(): TurnoConfig | undefined {
    return this.turni.find((t) => t.value === this.turno);
  }

  get selectedTurnoProduct(): OhelCampTurnoProduct | undefined {
    return this.turnoProducts.find((p) => p.turno === this.turno);
  }

  getTurnoPrice(turnoValue: string): string | null {
    if (turnoValue === 'children' || turnoValue === 'sorrisi-speciali') {
      return 'Su richiesta';
    }
    const product = this.turnoProducts.find((p) => p.turno === turnoValue);
    if (!product) return null;
    return this.formatPrice(product.totalAmount);
  }

  getAgeAtDecember31(): number | null {
    if (!this.dataNascita) return null;
    const birth = new Date(this.dataNascita);
    const dec31 = new Date(2026, 11, 31); // 31 dicembre 2026
    let age = dec31.getFullYear() - birth.getFullYear();
    const monthDiff = dec31.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && dec31.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  }

  isTurnoAvailableForAge(turno: TurnoConfig): boolean {
    const age = this.getAgeAtDecember31();
    if (age === null) return true;
    return age >= turno.minAge - 1 && age <= turno.maxAge + 1;
  }

  get availableTurni(): TurnoConfig[] {
    return this.turni.filter((t) => this.isTurnoAvailableForAge(t));
  }

  getTurnoProduct(turnoValue: string): OhelCampTurnoProduct | undefined {
    return this.turnoProducts.find((p) => p.turno === turnoValue);
  }

  get isAsafTurno(): boolean {
    return this.turno === 'asaf';
  }

  get isChildrenTurno(): boolean {
    return this.turno === 'children';
  }

  get isSorrisiSpecialiTurno(): boolean {
    return this.turno === 'sorrisi-speciali';
  }

  get requiresPayment(): boolean {
    return !this.isChildrenTurno && !this.isSorrisiSpecialiTurno;
  }

  formatPrice(cents: number): string {
    return '\u20AC' + (cents / 100).toFixed(2).replace('.', ',');
  }

  get depositFormatted(): string {
    const product = this.selectedTurnoProduct;
    if (!product) return '';
    return this.formatPrice(product.depositAmount);
  }

  get totalFormatted(): string {
    const product = this.selectedTurnoProduct;
    if (!product) return '';
    return this.formatPrice(product.totalAmount);
  }

  get remainingFormatted(): string {
    const product = this.selectedTurnoProduct;
    if (!product) return '';
    return this.formatPrice(product.totalAmount - product.depositAmount);
  }

  nextStep() {
    if (this.currentStep == 1) {
      // Show WhatsApp help button after 8 seconds
      setTimeout(() => {
        this.showWhatsappBtn = true;
      }, 8000);
    }
    if (this.currentStep < 6) {
      this.currentStep++;
      this.scrollToForm();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      // Reset turno if going back to before turno step (age might change)
      if (this.currentStep < 3) {
        this.turno = '';
      }
      this.scrollToForm();
    }
  }

  scrollToForm() {
    setTimeout(() => {
      document
        .querySelector('.registration-form')
        ?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  isStep1Valid(): boolean {
    return !!this.tipoIscrizione;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  isStep2Valid(): boolean {
    if (
      !this.cognome ||
      !this.nome ||
      !this.luogoNascita ||
      !this.dataNascita ||
      !this.residenza ||
      !this.via ||
      !this.cap ||
      !this.codiceFiscale ||
      !this.telefono ||
      !this.email ||
      !this.reperibilita
    ) {
      return false;
    }

    if (!this.isValidEmail(this.email)) {
      return false;
    }

    if (this.isMinore) {
      if (
        !this.nomeGenitore ||
        !this.cognomeGenitore ||
        !this.luogoNascitaGenitore ||
        !this.dataNascitaGenitore ||
        !this.residenzaGenitore ||
        !this.viaGenitore ||
        !this.capGenitore ||
        !this.emailGenitore
      ) {
        return false;
      }
      if (!this.isValidEmail(this.emailGenitore)) {
        return false;
      }
    }

    return true;
  }

  isStep3Valid(): boolean {
    if (!this.turno) return false;
    // Ensure selected turno is still valid for the age
    const turnoConfig = this.turni.find((t) => t.value === this.turno);
    if (turnoConfig && !this.isTurnoAvailableForAge(turnoConfig)) return false;
    // Campi obbligatori per turno Asaf
    if (this.isAsafTurno) {
      if (!this.strumentoAsaf.trim() || !this.motivazioneAsaf.trim())
        return false;
    }
    return true;
  }

  isStep4Valid(): boolean {
    if (!this.vaccinazioneAntitetanica) return false;
    return true;
  }

  isStep5Valid(): boolean {
    // Tessera sanitaria partecipante: sempre obbligatoria
    if (!this.tesseraSanitariaFronte || !this.tesseraSanitariaRetro)
      return false;
    if (this.isMinore) {
      // Minore: documento identità partecipante facoltativo
      // Genitore: tutto obbligatorio
      if (
        !this.documentoIdentitaFronteGenitore ||
        !this.documentoIdentitaRetroGenitore
      )
        return false;
      if (
        !this.tesseraSanitariaFronteGenitore ||
        !this.tesseraSanitariaRetroGenitore
      )
        return false;
    } else {
      // Adulto: documento identità obbligatorio
      if (!this.documentoIdentitaFronte || !this.documentoIdentitaRetro)
        return false;
    }
    return true;
  }

  isStep6Valid(): boolean {
    if (!this.consensoSocial) return false;
    if (!this.consensoTrattamentoDati) return false;
    if (!this.consensoDiffusioneDati) return false;
    if (!this.consensoTrattamentoDatiSanitari) return false;
    if (!this.consensoEsonero) return false;
    return true;
  }

  get isAnyUploading(): boolean {
    return !!this.uploadingSlot;
  }

  isFormValid(): boolean {
    return (
      this.isStep1Valid() &&
      this.isStep2Valid() &&
      this.isStep3Valid() &&
      this.isStep4Valid() &&
      this.isStep5Valid() &&
      this.isStep6Valid()
    );
  }

  async onSubmit() {
    if (!this.isFormValid() || this.isSubmitting) return;

    this.isSubmitting = true;
    this.errorMessage = '';
    this.canRetry = false;

    const registrationData: Record<string, unknown> = {
      tipoIscrizione: this.tipoIscrizione,
      turno: this.turno,
      cognome: this.cognome.trim(),
      nome: this.nome.trim(),
      luogoNascita: this.luogoNascita.trim(),
      dataNascita: this.dataNascita,
      residenza: this.residenza?.trim() || '',
      via: this.via?.trim() || '',
      cap: this.cap?.trim() || '',
      codiceFiscale: this.codiceFiscale.trim().toUpperCase(),
      telefono: this.telefono.trim(),
      email: this.email.trim().toLowerCase(),
      reperibilita: this.reperibilita?.trim() || '',
      note: this.note?.trim() || '',
      strumentoAsaf: this.isAsafTurno ? this.strumentoAsaf.trim() : '',
      motivazioneAsaf: this.isAsafTurno ? this.motivazioneAsaf.trim() : '',
      vaccinazioneAntitetanica: this.vaccinazioneAntitetanica,
      allergie: this.allergie?.trim() || '',
      farmaci: this.farmaci?.trim() || '',
      patologie: this.patologie?.trim() || '',
      noteSanitarie: this.noteSanitarie?.trim() || '',
      consensoSocial: this.consensoSocial,
      consensoTrattamentoDati: this.consensoTrattamentoDati,
      consensoDiffusioneDati: this.consensoDiffusioneDati,
      consensoPromozione: this.consensoPromozione,
      consensoTrattamentoDatiSanitari: this.consensoTrattamentoDatiSanitari,
      consensoEsonero: this.consensoEsonero,
      consensoWhatsapp: this.consensoWhatsapp,
      documentoIdentitaFronte: this.documentoIdentitaFronte,
      documentoIdentitaRetro: this.documentoIdentitaRetro,
      tesseraSanitariaFronte: this.tesseraSanitariaFronte,
      tesseraSanitariaRetro: this.tesseraSanitariaRetro,
    };

    if (this.isMinore) {
      registrationData['nomeGenitore'] = this.nomeGenitore.trim();
      registrationData['cognomeGenitore'] = this.cognomeGenitore.trim();
      registrationData['luogoNascitaGenitore'] =
        this.luogoNascitaGenitore?.trim() || '';
      registrationData['dataNascitaGenitore'] = this.dataNascitaGenitore || '';
      registrationData['residenzaGenitore'] =
        this.residenzaGenitore?.trim() || '';
      registrationData['viaGenitore'] = this.viaGenitore?.trim() || '';
      registrationData['capGenitore'] = this.capGenitore?.trim() || '';
      registrationData['emailGenitore'] = this.emailGenitore?.trim() || '';
      registrationData['documentoIdentitaFronteGenitore'] =
        this.documentoIdentitaFronteGenitore;
      registrationData['documentoIdentitaRetroGenitore'] =
        this.documentoIdentitaRetroGenitore;
      registrationData['tesseraSanitariaFronteGenitore'] =
        this.tesseraSanitariaFronteGenitore;
      registrationData['tesseraSanitariaRetroGenitore'] =
        this.tesseraSanitariaRetroGenitore;
    }

    // Turni speciali senza pagamento online
    if (!this.requiresPayment) {
      this.stripeService
        .submitRegistrationWithoutPayment(registrationData)
        .subscribe({
          next: (response) => {
            this.isSubmitting = false;
            if (response.success) {
              this.isSuccess = true;
              if (this.isChildrenTurno) {
                this.successMessage =
                  "Iscrizione inviata con successo! Riceverai a breve un'email con le istruzioni per il pagamento del turno Children.";
              } else if (this.isSorrisiSpecialiTurno) {
                this.successMessage =
                  'Iscrizione inviata con successo! Sarai contattato a breve per il colloquio preliminare e la conferma.';
              }
            } else {
              this.errorMessage =
                response.error || "Errore durante l'invio dell'iscrizione";
              this.canRetry = true;
            }
          },
          error: (error: OhelCampStripeError) => {
            this.isSubmitting = false;
            this.handlePaymentError(error);
          },
        });
      return;
    }

    // Turni con pagamento online
    try {
      await this.stripeService.processPayment(registrationData);
    } catch (error) {
      this.isSubmitting = false;
      this.handlePaymentError(error as OhelCampStripeError);
    }
  }

  private handlePaymentError(error: OhelCampStripeError): void {
    this.canRetry = error.retryable;

    switch (error.type) {
      case 'validation':
        this.errorMessage = error.message;
        break;
      case 'network':
        this.errorMessage =
          'Problema di connessione. Verifica la tua rete e riprova.';
        break;
      case 'timeout':
        this.errorMessage =
          'La richiesta sta impiegando troppo tempo. Riprova tra poco.';
        break;
      case 'stripe':
        this.errorMessage =
          'Errore nel sistema di pagamento. Riprova tra qualche minuto.';
        break;
      default:
        this.errorMessage =
          error.message || 'Si e verificato un errore. Riprova piu tardi.';
    }

    console.error('Errore pagamento:', error);
  }

  retryPayment(): void {
    if (this.canRetry) {
      this.onSubmit();
    }
  }

  onFileSelected(
    event: Event,
    slot: string,
    tipoDocumento: string,
    soggetto: string,
  ): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be re-selected
    input.value = '';

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/heic',
      'image/heif',
      'application/pdf',
    ];
    // iOS camera may produce files with empty type; allow if extension matches
    const fileType = file.type || this.inferFileType(file.name);
    if (!allowedTypes.includes(fileType)) {
      this.uploadErrors[slot] =
        'Formato non supportato. Usa JPG, PNG, WEBP o PDF.';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.uploadErrors[slot] = 'File troppo grande. Massimo 10MB.';
      return;
    }

    delete this.uploadErrors[slot];

    // Generate preview
    if (fileType.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      this.uploadPreviews[slot] = url;
    } else {
      this.uploadPreviews[slot] = 'pdf';
    }

    // Delay upload slightly: on iOS, the browser is suspended while the camera
    // is active. When it resumes, the network connection may not be ready yet.
    setTimeout(() => {
      this.uploadFile(file, slot, tipoDocumento, soggetto);
    }, 500);
  }

  private inferFileType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const map: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      webp: 'image/webp',
      heic: 'image/heic',
      heif: 'image/heif',
      pdf: 'application/pdf',
    };
    return map[ext || ''] || '';
  }

  private uploadFile(
    file: File,
    slot: string,
    tipoDocumento: string,
    soggetto: string,
    retryCount: number = 0,
  ): void {
    this.uploadingSlot = slot;

    const nomeCompleto =
      soggetto === 'genitore'
        ? `${this.cognomeGenitore} ${this.nomeGenitore}`.trim()
        : `${this.cognome} ${this.nome}`.trim();

    this.stripeService
      .uploadDocument(file, tipoDocumento, soggetto, nomeCompleto)
      .subscribe({
        next: (response) => {
          this.uploadingSlot = '';
          if (response.success && response.documentId) {
            (this as Record<string, unknown>)[slot] = response.documentId;
            this.uploadFilenames[slot] = response.filename || file.name;
          } else {
            this.uploadErrors[slot] =
              response.error || 'Errore nel caricamento';
            this.clearPreview(slot);
          }
        },
        error: (error) => {
          // Retry once on network errors (common on iOS after camera capture)
          if (
            retryCount < 1 &&
            (error?.type === 'network' || error?.type === 'timeout')
          ) {
            setTimeout(() => {
              this.uploadFile(
                file,
                slot,
                tipoDocumento,
                soggetto,
                retryCount + 1,
              );
            }, 1500);
            return;
          }
          this.uploadingSlot = '';
          this.uploadErrors[slot] =
            error?.message || 'Errore nel caricamento del file';
          this.clearPreview(slot);
        },
      });
  }

  removeDocument(slot: string): void {
    (this as Record<string, unknown>)[slot] = '';
    delete this.uploadErrors[slot];
    delete this.uploadFilenames[slot];
    this.clearPreview(slot);
  }

  private clearPreview(slot: string): void {
    const preview = this.uploadPreviews[slot];
    if (preview && preview !== 'pdf') {
      URL.revokeObjectURL(preview);
    }
    delete this.uploadPreviews[slot];
  }

  resetForm() {
    this.tipoIscrizione = '';
    this.turno = '';
    this.cognome = '';
    this.nome = '';
    this.luogoNascita = '';
    this.dataNascita = '';
    this.residenza = '';
    this.via = '';
    this.cap = '';
    this.codiceFiscale = '';
    this.telefono = '';
    this.email = '';
    this.reperibilita = '';
    this.note = '';
    this.strumentoAsaf = '';
    this.motivazioneAsaf = '';
    this.nomeGenitore = '';
    this.cognomeGenitore = '';
    this.luogoNascitaGenitore = '';
    this.dataNascitaGenitore = '';
    this.residenzaGenitore = '';
    this.viaGenitore = '';
    this.capGenitore = '';
    this.emailGenitore = '';
    this.vaccinazioneAntitetanica = '';
    this.allergie = '';
    this.farmaci = '';
    this.patologie = '';
    this.noteSanitarie = '';
    this.consensoSocial = '';
    this.consensoTrattamentoDati = false;
    this.consensoDiffusioneDati = false;
    this.consensoPromozione = false;
    this.consensoTrattamentoDatiSanitari = false;
    this.consensoEsonero = false;
    this.consensoWhatsapp = false;
    // Cleanup document previews
    for (const key of Object.keys(this.uploadPreviews)) {
      this.clearPreview(key);
    }
    this.documentoIdentitaFronte = '';
    this.documentoIdentitaRetro = '';
    this.tesseraSanitariaFronte = '';
    this.tesseraSanitariaRetro = '';
    this.documentoIdentitaFronteGenitore = '';
    this.documentoIdentitaRetroGenitore = '';
    this.tesseraSanitariaFronteGenitore = '';
    this.tesseraSanitariaRetroGenitore = '';
    this.uploadingSlot = '';
    this.uploadErrors = {};
    this.uploadPreviews = {};
    this.uploadFilenames = {};
    this.isSuccess = false;
    this.successMessage = '';
    this.errorMessage = '';
    this.currentStep = 1;
  }

  openPrivacyDialog() {
    this.showPrivacyDialog = true;
  }

  closePrivacyDialog() {
    this.showPrivacyDialog = false;
  }
}
